import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { agentsApi } from '../../api/agents.api';
import type { Agent } from '../../api/agents.api';
import { comptesApi } from '../../api/clients.api';
import type { Compte } from '../../api/clients.api';
import { toast } from '../../hooks/use-toast';
import { Loader2 } from 'lucide-react';

interface AssignAgentModalProps {
    isOpen: boolean;
    onClose: () => void;
    compte: Compte | null;
    onSuccess: (updatedCompte: Compte) => void;
}

export function AssignAgentModal({ isOpen, onClose, compte, onSuccess }: AssignAgentModalProps) {
    const [agents, setAgents] = useState<Agent[]>([]);
    const [selectedAgentId, setSelectedAgentId] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [isFetchingAgents, setIsFetchingAgents] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setIsFetchingAgents(true);
        agentsApi.getAll()
            .then(res => {
                const active = (res.agents || []).filter((a: Agent) => a.actif);
                setAgents(active);
            })
            .catch(() => toast({ title: 'Erreur', description: 'Pa ka chaje ajan yo', type: 'error' }))
            .finally(() => setIsFetchingAgents(false));
    }, [isOpen]);

    useEffect(() => {
        setSelectedAgentId(compte?.agentId ?? '');
    }, [compte]);

    const handleSubmit = async () => {
        if (!compte) return;
        setIsLoading(true);
        const effectiveAgentId = selectedAgentId === '__UNASSIGN__' ? null : selectedAgentId;
        try {
            let result;
            if (compte.agentId) {
                result = await comptesApi.reassignAgent(compte.id, effectiveAgentId);
            } else {
                result = await comptesApi.assignAgent(compte.id, effectiveAgentId!);
            }
            toast({ title: '✅ Chanjman efektye', description: result.message });
            onSuccess(result.compte);
            onClose();
        } catch (err: any) {
            toast({
                title: 'Erreur',
                description: err?.response?.data?.message || 'Chanjman echwe',
                type: 'error',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const isAssigned = !!compte?.agentId;
    const title = isAssigned ? 'Chanje / Libere Ajan' : 'Asiye Ajan';
    const canSubmit = isAssigned ? true : (!!selectedAgentId && selectedAgentId !== '__UNASSIGN__');

    return (
        <Dialog open={isOpen} onOpenChange={v => { if (!v) onClose(); }}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>
                        Kont: <strong>{compte?.code}</strong>
                        {isAssigned && (
                            <span className="ml-2 text-amber-600 text-xs">(Ajan aktyèl asiye)</span>
                        )}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div>
                        <Label className="text-sm font-semibold mb-1 block">Chwazi Ajan</Label>
                        {isFetchingAgents ? (
                            <div className="flex items-center gap-2 text-slate-400 text-sm py-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Chajman ajan yo…
                            </div>
                        ) : (
                            <Select value={selectedAgentId} onValueChange={setSelectedAgentId}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Chwazi yon ajan…" />
                                </SelectTrigger>
                                <SelectContent>
                                    {isAssigned && (
                                        <SelectItem value="__UNASSIGN__">
                                            <span className="text-red-500 font-medium">
                                                🔓 Libere kont sa (pa gen ajan)
                                            </span>
                                        </SelectItem>
                                    )}
                                    {agents.map(agent => (
                                        <SelectItem key={agent.id} value={agent.id}>
                                            {agent.code} — {agent.client?.prenom} {agent.client?.nom}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="ghost" onClick={onClose} disabled={isLoading}>
                            Anile
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={isLoading || isFetchingAgents || !canSubmit}
                        >
                            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {isAssigned
                                ? (selectedAgentId ? 'Reasiye' : 'Libere Kont')
                                : 'Asiye Ajan'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
